import useAPI from "@toothfairy/shared-api/useApi";
import { AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppChoice from "@toothfairy/shared-ui/AppChoice";
import AppContactForm from "@toothfairy/shared-ui/AppContactForm";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppPricing from "@toothfairy/shared-ui/AppPricing";
import AppTextInput from "@toothfairy/shared-ui/AppTextInput";
import AppPricingScreen from "./pricing/AppPricingScreen";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppSeparator from "@toothfairy/shared-ui/AppSeparator";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import axios from "axios";
import envConfig from "../../../../envConfig";
import API_Consumption from "../../../API/API_Consumption";
import Emails from "../../../API/Emails";
import Files from "../../../API/Files";
import trainingAPI from "../../../API/Training/trainingAPI";
import workspacesHelper from "../../../API/Workspaces/workspacesHelper";
import ImagePlaceholder from "../../../Components/ImagePlaceholder";
import ToothFairyButton from "../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyLoadingPlaceholder from "../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import ToothFairyModal from "../../../Components/ToothFairyComponents/ToothFairyModal";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import Color from "../../../Utility/Color";
import { useModels, useExternalModels } from "../../Common/baseModels";
import colorsMappings from "../../Common/colorsMappings";
import { normalizeBlockedModels } from "../../../Utils/modelRestrictions";
import utils from "../../Common/utils";
import { isLargeScreen } from "../../MainViewContainer";
import {
  isBillingAddressSet,
  isUserWithNoPaymentMethod,
  isSSO,
} from "../../../../App";
import { useHistory } from "../../../Router";
import { convertImageToPng } from "../../../Utility/imageUtils";
import {
  DEFAULT_NEW_WORKSPACE_SEATS,
  ENABLE_CONSUMPTION_BILLING,
  MAX_WORKSPACE_UOI_BUDGET,
  formatUoICompact,
  parseUoICompact,
} from "../../../Constants/billing";

const UOI_PER_BLOCK = { business: 5000, enterprise: 7500 };
const PRO_UOI_PER_SEAT = 1500;

const formatNumber = (num) => {
  return num?.toLocaleString("en-US") || "0";
};

const isUoIPlan = (subscriptionType) => {
  return ["pro", "business", "enterprise"].includes(
    subscriptionType?.toLowerCase(),
  );
};

const convertSeatsToUoIDisplay = (subscriptionType, seats) => {
  if (subscriptionType?.toLowerCase() === "pro")
    return seats * PRO_UOI_PER_SEAT;
  return seats;
};

const getUnitLabel = (subscriptionType, count) => {
  if (isUoIPlan(subscriptionType)) {
    return "UoI";
  }
  return count > 1 ? "seats" : "seat";
};

const convertUoIToMaxUsers = (subscriptionType, uoiValue) => {
  const block = UOI_PER_BLOCK[subscriptionType?.toLowerCase()];
  if (block) return Math.ceil(uoiValue / block);
  return uoiValue;
};

const convertMaxUsersToUoI = (subscriptionType, maxUsers) => {
  const block = UOI_PER_BLOCK[subscriptionType?.toLowerCase()];
  if (block) return maxUsers * block;
  return maxUsers;
};

const WorkspaceFormView = React.memo(
  ({
    onboardingData,
    workspaceCreationData,
    modalContentKey,
    cancelPlanChange = () => {},
    targetPlan,
    setTargetPlan,
    selectedWorkspace,
    onPlanSelection = () => {},
    onLogoChange = () => {},
    workspaceLogo: workspaceLogoProp,
    setWorkspaceLogo: setWorkspaceLogoProp,
    fileUploadInProgress: fileUploadInProgressProp,
    setFileUploadInProgress: setFileUploadInProgressProp,
    fileUploadProgress: fileUploadProgressProp,
    setFileUploadProgress: setFileUploadProgressProp,
    setWorkspaceLogoFilename = () => {},
    eligibleParentWorkspaces = [],
  }) => {
    const {
      values,
      initialValues,
      setFieldValue,
    } = AppForms.useFormikContext();

    const { state } = AppStateManager.useAppStateManager();
    const [showPlan, setShowPlan] = React.useState(false);
    const { user } = AppUser.useUser();
    const history = useHistory();
    const [selectedPlan, setSelectedPlan] = React.useState(null);
    const [confirmationRequired, setConfirmationRequired] = React.useState(
      false,
    );
    const [contactRequired, setContactRequired] = React.useState(false);
    const [sendingInProgress, setSendingInProgress] = React.useState(false);
    const [updateInProgress, setUpdateInProgress] = React.useState(false);
    const [tempPayload, setTempPayload] = React.useState(null);
    const [updateRequired, setUpdateRequired] = React.useState(false);
    const [localWorkspaceLogo, setLocalWorkspaceLogo] = React.useState(null);
    const [
      localFileUploadInProgress,
      setLocalFileUploadInProgress,
    ] = React.useState(false);
    const [
      localFileUploadProgress,
      setLocalFileUploadProgress,
    ] = React.useState(0);

    const workspaceLogo = workspaceLogoProp ?? localWorkspaceLogo;
    const setWorkspaceLogo = setWorkspaceLogoProp ?? setLocalWorkspaceLogo;
    const fileUploadInProgress =
      fileUploadInProgressProp ?? localFileUploadInProgress;
    const setFileUploadInProgress =
      setFileUploadInProgressProp ?? setLocalFileUploadInProgress;
    const fileUploadProgress =
      fileUploadProgressProp ?? localFileUploadProgress;
    const setFileUploadProgress =
      setFileUploadProgressProp ?? setLocalFileUploadProgress;
    const creditBalance = user?.stripeCustomerData?.balance / -100;
    const isBillingDisabled =
      (isUserWithNoPaymentMethod() || !isBillingAddressSet()) &&
      !isSSO() &&
      creditBalance < 0;
    const currencyFormatter = (value) => {
      let formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      });
      return formatter.format(value);
    };
    const {
      data,
      loading,
      apiRequest: updateUsageForWorkspaceRequest,
      response,
    } = useAPI(API_Consumption.updateUsageForWorkspace, envConfig);
    const isNew = modalContentKey === "NEW";
    const hasParentWorkspace =
      values?.parentWorkspaceID && values?.parentWorkspaceID !== "none";
    const isChildWorkspace = hasParentWorkspace;
    const parentWorkspace = useMemo(
      () =>
        isChildWorkspace
          ? eligibleParentWorkspaces.find(
              (ws) => ws.id === values.parentWorkspaceID,
            ) ||
            state.workspaces.find((ws) => ws.id === values.parentWorkspaceID)
          : null,
      [
        isChildWorkspace,
        eligibleParentWorkspaces,
        values.parentWorkspaceID,
        state.workspaces,
      ],
    );
    const isChangeOfSubRequested =
      values?.targetSubscriptionType !== null &&
      values?.targetSubscriptionType !== values?.subscriptionType;
    const {
      getActiveWorkspace,
      canUpdateSubscription,
      getEffectiveSubscriptionType,
    } = useWorkspaces();
    const _targetPlan =
      targetPlan || getActiveWorkspace()?.targetSubscriptionType;
    const { currentTheme } = AppTheme.useTheme();
    const { t } = useTranslation();
    const customDomains =
      getActiveWorkspace()?.customGenerationConfig?.domains || [];
    const isMessageVisible = useMemo(
      () =>
        (initialValues.subscriptionType !== values.subscriptionType ||
          isChangeOfSubRequested) &&
        !isNew,
      [
        initialValues.subscriptionType,
        values.subscriptionType,
        isChangeOfSubRequested,
        isNew,
      ],
    );
    const isChangeCancellable = isChangeOfSubRequested && !isNew;
    const AppToasty = AppToast.useToast();

    const [operatorAgents, setOperatorAgents] = React.useState([]);

    // Model options offered for the workspace-level blocklist: TF serverless
    // base models plus external-provider models (fine-tuned models are exempt
    // from the blocklist and are therefore not offered).
    const { baseModels: _baseModelsForBlocklist } = useModels();
    const { externalModels: _externalModelsForBlocklist } = useExternalModels();
    const _blockableModels = useMemo(
      () => [
        ...(_baseModelsForBlocklist || []).map((m) => ({
          id: m?.id,
          name: m?.name || m?.id,
          modelType: m?.modelType,
        })),
        ...(_externalModelsForBlocklist || []).map((m) => ({
          id: m?.id,
          name: m?.name || m?.id,
          modelType: m?.modelType,
        })),
      ],
      [_baseModelsForBlocklist, _externalModelsForBlocklist],
    );

    // Stored blockedModels come in as id-only entries (plain strings or
    // minimal {id} objects); upgrade them onto the real option objects so the
    // multi-select chips render the friendly name + colour. One-way sync: only
    // rewrite when a stored id doesn't already match its friendly option.
    useEffect(() => {
      const current = values?.blockedModels || [];
      const normalized = normalizeBlockedModels(current, _blockableModels);
      const needsUpgrade =
        current.length !== normalized.length ||
        current.some((c, i) => {
          const cid = typeof c === "string" ? c : c?.id || c?.value;
          return cid !== normalized[i]?.id || !normalized[i]?.backgroundColor;
        });
      if (needsUpgrade && _blockableModels.length > 0) {
        setFieldValue("blockedModels", normalized);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_blockableModels]);

    // Multi-select styling matching the repo's other AppFormMultiSelect
    // usages (SiteSettingsView / FunctionSettingsAIView). Model chips enforce
    // their size-tier colour (mapBackgroundColorWithModelSize on the option);
    // entries missing a backgroundColor (id-only values on first render after
    // a reload, before the upgrade effect runs) fall back to primary.
    const colourStyles = {
      control: (styles) => ({
        ...styles,
        backgroundColor: currentTheme?.theme?.inputBackgroundColor,
        borderColor: currentTheme?.theme?.border_color,
        fontFamily: "normalFont",
        fontSize: currentTheme?.theme?.small_font_size,
        borderRadius: 10,
      }),
      option: (styles, { data, isDisabled, isFocused, isSelected }) => {
        let color = new Color(data?.backgroundColor || "#1DB7C5");
        return {
          ...styles,
          backgroundColor: isDisabled
            ? undefined
            : isSelected
            ? color
            : isFocused
            ? color.alpha(0.1).css()
            : undefined,
          color: isDisabled
            ? "#ccc"
            : isSelected
            ? currentTheme?.theme?.white
              ? "white"
              : "black"
            : data?.backgroundColor || "#1DB7C5",
          cursor: isDisabled ? "not-allowed" : "default",
          ":active": {
            ...styles[":active"],
            backgroundColor: !isDisabled
              ? isSelected
                ? data?.backgroundColor || "#1DB7C5"
                : color.alpha(0.3).css()
              : undefined,
          },
        };
      },
      multiValue: (styles, { data }) => {
        let color = new Color(data?.backgroundColor || "#1DB7C5");
        return {
          ...styles,
          backgroundColor: color.alpha(0.1).css(),
        };
      },
      multiValueLabel: (styles, { data }) => ({
        ...styles,
        color: data?.backgroundColor || "#1DB7C5",
      }),
      multiValueRemove: (styles, { data }) => ({
        ...styles,
        color: data?.backgroundColor || "#1DB7C5",
        ":hover": {
          backgroundColor: data?.backgroundColor || "#1DB7C5",
          color: "white",
        },
      }),
      menu: (styles) => ({
        ...styles,
        backgroundColor: currentTheme?.theme?.inputBackgroundColor,
        borderColor: currentTheme?.theme?.lightGray,
        fontFamily: "normalFont",
        fontSize: currentTheme?.theme?.small_font_size,
      }),
    };

    useEffect(() => {
      if (isChildWorkspace && parentWorkspace) {
        setFieldValue("subscriptionType", parentWorkspace.subscriptionType);
        setFieldValue("maxUsers", parentWorkspace.maxUsers);
        setFieldValue(
          "billingProvider",
          parentWorkspace.billingProvider || "STRIPE",
        );
      } else if (
        isNew &&
        values?.subscriptionType === parentWorkspace?.subscriptionType
      ) {
        setFieldValue(
          "subscriptionType",
          initialValues?.subscriptionType ||
            (ENABLE_CONSUMPTION_BILLING ? "business" : "pro"),
        );
        setFieldValue(
          "maxUsers",
          initialValues?.maxUsers || DEFAULT_NEW_WORKSPACE_SEATS,
        );
        setFieldValue(
          "billingProvider",
          initialValues?.billingProvider || "STRIPE",
        );
      }
    }, [
      values?.parentWorkspaceID,
      parentWorkspace,
      isChildWorkspace,
      isNew,
      setFieldValue,
      initialValues?.subscriptionType,
      initialValues?.maxUsers,
    ]);

    React.useEffect(() => {
      const loadOperatorAgents = () => {
        const operators =
          state?.agents?.filter(
            (a) =>
              a?.agentType === "Chatbot" &&
              a?.mode === "retriever" &&
              !a?._deleted,
          ) || [];

        const agentOptions = [
          {
            name: t("settings.workspace.defaultChatAgentDefault"),
            id: null,
          },
          ...operators.map((a) => ({
            name: a.label,
            id: a.id,
          })),
        ];

        setOperatorAgents(agentOptions);
      };

      loadOperatorAgents();
    }, [state?.agents, t]);

    const {
      data: S3uploadUrlData,
      loading: urlUploadGenerationInProgress,
      apiRequest: S3uploadUrlRequest,
      response: S3uploadResponse,
    } = useAPI(trainingAPI.bulkImportUrlGeneration, envConfig);

    const {
      data: S3downloadUrlData,
      loading: urldownloadGenerationInProgress,
      apiRequest: S3downloadUrlRequest,
      response: S3downloadResponse,
    } = useAPI(Files.downloadUrlGeneration, envConfig);

    const handleLogoSelection = async (file) => {
      if (!file) {
        setFieldValue("logo", null);
        setWorkspaceLogo(null);
        setWorkspaceLogoFilename(null);
        return;
      }

      let processedFile = file;
      if (file.type.startsWith("image/") && file.type !== "image/png") {
        try {
          processedFile = await convertImageToPng(file);
        } catch (error) {
          console.error("Failed to convert image:", error);
          AppToasty.show(t("toasts.logo.convertFailed"), {
            type: "danger",
          });
          return;
        }
      }

      const filename = processedFile.name;
      const _fileN = String(Date.now()) + filename;
      const contentType = filename.slice(
        (Math.max(0, filename.lastIndexOf(".")) || Infinity) + 1,
      );

      try {
        const result = await S3uploadUrlRequest({
          filename: `${getActiveWorkspace()?.id}/${_fileN}`,
          importType: "imported-image",
          contentType,
        });

        const upload = await axios.request({
          method: "put",
          url: JSON.parse(result?.body)?.uploadURL,
          data: processedFile,
          headers: {
            "Content-Type": processedFile.type,
          },
          onUploadProgress: (p) => {
            setFileUploadInProgress(true);
            setFileUploadProgress(Math.round((p.loaded * 100) / p.total));
            if (p.loaded === p.total) {
              setFileUploadInProgress(false);
              setFileUploadProgress(100);
            }
          },
        });

        if (JSON.parse(result?.body).uploadURL) {
          setFieldValue("logo", _fileN);
          setWorkspaceLogoFilename(_fileN);
          try {
            const _url = await S3downloadUrlRequest({
              filename: `imported-image/${getActiveWorkspace()?.id}/${_fileN}`,
              context: "pdf",
              workspaceid: getActiveWorkspace()?.id,
            });
            if (_url?.url) {
              setWorkspaceLogo(_url.url);
            }
          } catch (downloadError) {
            console.error(
              "Error fetching logo URL after upload:",
              downloadError,
            );
          }
          AppToasty.show(t("toasts.logo.uploadSuccess"), {
            type: "success",
          });
        }
      } catch (error) {
        console.error("Error uploading logo:", error);
        AppToasty.show(t("toasts.logo.uploadFailed"), {
          type: "danger",
        });
        setFileUploadInProgress(false);
      }
    };

    const _modal = showPlan ? (
      <ToothFairyModal
        headerContainerStyle={{
          paddingTop: 20,
          paddingHorizontal: 20,
        }}
        scrollViewStyle={{
          margin: 20,
          width: "95%",
          overflow: updateInProgress ? "hidden" : "auto",
          overflowX: "hidden",
        }}
        modalBackdrop={true}
        modalBackdropStatic={true}
        modalTitle={
          modalContentKey === "UPDATE"
            ? getEffectiveSubscriptionType(getActiveWorkspace()) ===
              "enterprise"
              ? t("workspaces.updateYourPlan")
              : t("pricing.enterpriseUpgradeTitle", {
                  defaultValue: "Unlock Enterprise Tier",
                })
            : null
        }
        modalStyle={{
          width: contactRequired
            ? "unset"
            : confirmationRequired
            ? 650
            : modalContentKey === "UPDATE" &&
              getEffectiveSubscriptionType(getActiveWorkspace()) !==
                "enterprise"
            ? 560
            : "90%",
          maxWidth: "unset",
          margin: "auto",
          height: contactRequired
            ? "fit-content"
            : updateInProgress
            ? 280
            : !confirmationRequired
            ? "auto"
            : "unset",
          maxHeight: confirmationRequired ? 460 : "90%",
          overflow:
            updateInProgress || confirmationRequired || contactRequired
              ? "hidden"
              : "auto",
          overflowX: "hidden",
        }}
        closeModal={() => {
          setShowPlan(false);
          setContactRequired(false);
          setUpdateRequired(false);
        }}
        isModalVisible={showPlan}
        transparent={true}
      >
        {updateInProgress ? (
          <ToothFairyLoadingPlaceholder
            style={{
              maxHeight: 300,
              position: "relative",
            }}
            caption="messages.planUpdateInProgress"
          />
        ) : contactRequired ? (
          <AppContactForm
            sendingInProgress={sendingInProgress}
            email={user?.user_email}
            enquiryType="Enterprise upgrade"
            onSubmitEvent={async (values) => {
              try {
                setSendingInProgress(true);
                const emailSendResult = await Emails.sendEmail({
                  userEmail: user?.user_email,
                  subject: values?.enquiryType,
                  message: values?.message,
                  workspaceId: getActiveWorkspace()?.id,
                });
                const name = user?.username || user?.user_email;
                const email = user?.user_email;
                const enquiry = values?.enquiryType;
                const data = {
                  fields: [
                    {
                      name: "email",
                      value: email,
                    },
                    {
                      name: "firstname",
                      value: name,
                    },
                    {
                      name: "enquiry_type",
                      value: enquiry,
                    },
                    {
                      name: "message",
                      value: values?.message,
                    },
                  ],
                  context: {
                    pageUri: "www.app.toothfairyai.com/pricing",
                    pageName: "Pricing",
                  },
                };
                let url = `https://api.hsforms.com/submissions/v3/integration/submit/40158468/4f2f88c6-dbcd-498d-8abf-3325acb782ca`;
                const _hub_result = await fetch(url, {
                  method: "POST",
                  body: JSON.stringify(data),
                  headers: {
                    "Content-Type": "application/json",
                  },
                });
                console.log("_hub_result", _hub_result);
                AppToasty.show(t("toasts.workspace.upgradeRequestSent"), {
                  type: "success",
                });
              } catch (error) {
                console.log("error", error);
                AppToasty.show(t("toasts.workspace.upgradeRequestFailed"), {
                  type: "danger",
                });
              } finally {
                setSendingInProgress(false);
                setShowPlan(false);
                setContactRequired(false);
                setUpdateRequired(false);
              }
            }}
            wrapperStyle={{
              padding: 20,
              backgroundColor: currentTheme?.theme?.transparent,
              margin: "auto",
              minWidth: isLargeScreen() && 600,
              borderRadius: 10,
            }}
            containerStyle={{
              borderRadius: 10,
            }}
          />
        ) : confirmationRequired ? (
          <AppChoice
            wrapperStyle={{
              alignSelf: "center",
              maxWidth: 600,
              margin: "auto",
              padding: 20,
              // backgroundColor: currentTheme?.theme?.lightGrey,
              borderRadius: 10,
            }}
            cancelCaption={t("workspaces.planChangeCancelCaption")}
            confirmCaption={t("workspaces.planChangeConfirmCaption")}
            onConfirmButtonPress={async () => {
              setUpdateInProgress(true);
              const { subscriptionType, seats } = tempPayload;
              try {
                // if the selection of seats is less than the users invited then show error

                if (
                  envConfig?.REACT_APP_ENV !== "dev" &&
                  !canUpdateSubscription() &&
                  subscriptionType !== getActiveWorkspace()?.subscriptionType
                )
                  return AppToasty.show(
                    t("toasts.workspace.subscriptionUpdateLimit"),
                    {
                      type: "danger",
                    },
                  );

                const updateWorkspace = await workspacesHelper.updateWorkspace(
                  getActiveWorkspace()?.id,
                  [
                    {
                      property: "maxUsers",
                      value: convertUoIToMaxUsers(subscriptionType, seats),
                    },
                    {
                      property: "subscriptionType",
                      value: subscriptionType,
                    },
                    ...(tempPayload?.isEnterpriseBudgetIncrease
                      ? [{ property: "customUoiBudget", value: seats }]
                      : []),
                  ],
                );

                if (updateWorkspace) {
                  const resultUpdateStripe = await updateUsageForWorkspaceRequest(
                    {
                      workspaceId: getActiveWorkspace()?.id,
                    },
                  );
                }
                setShowPlan(false);
                AppToasty.show(t("toasts.workspace.planUpdated"), {
                  type: "success",
                });
                setTimeout(() => {
                  window.location.reload();
                }, 2000);
              } catch (error) {
                console.log("error", error);
                AppToasty.show(t("toasts.workspace.planUpdateFailed"), {
                  type: "danger",
                });
              } finally {
                setConfirmationRequired(false);
                setTempPayload(null);
                setUpdateInProgress(false);
                setContactRequired(false);
              }
            }}
            onCancelButtonPress={() => {
              setConfirmationRequired(false);
              setTempPayload(null);
              setUpdateRequired(false);
              setContactRequired(false);
            }}
            AppChoiceButton={ToothFairyButton}
            caption={t("workspaces.planChangeCaption")}
            subCaption={`Once done no further changes can be made within the month`}
          >
            <View>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  marginTop: 10,
                }}
              >
                <AppText>{`Your new plan will be `}</AppText>
                <AppText
                  style={{
                    color: currentTheme?.theme?.primary,
                  }}
                  fontWeight="bold"
                >
                  {`${tempPayload?.subscriptionType?.toUpperCase()}`}
                </AppText>
                <AppText>{` with `}</AppText>
                <AppText
                  style={{
                    color: currentTheme?.theme?.primary,
                  }}
                  fontWeight="bold"
                >
                  {`${formatNumber(
                    convertSeatsToUoIDisplay(
                      tempPayload?.subscriptionType,
                      tempPayload?.seats,
                    ),
                  )}`}
                </AppText>
                <AppText>{` ${getUnitLabel(
                  tempPayload?.subscriptionType,
                  tempPayload?.seats,
                )}`}</AppText>
              </View>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  marginTop: 10,
                }}
              >
                <AppText>{`Your new monthly subscription cost will be: `}</AppText>
                <AppText
                  style={{
                    color: currentTheme?.theme?.primary,
                  }}
                  fontWeight="bold"
                >
                  {`${currencyFormatter(
                    tempPayload?.unitPrice * tempPayload?.seats,
                  )}`}
                </AppText>
              </View>
              {tempPayload?.isEnterpriseBudgetIncrease && (
                <View
                  style={{
                    marginTop: 12,
                    padding: 12,
                    backgroundColor:
                      currentTheme?.theme?.softBackgroundColor || "#FFF3E0",
                    borderRadius: 8,
                    borderLeftWidth: 3,
                    borderLeftColor: currentTheme?.theme?.orange || "#FF9800",
                  }}
                >
                  <AppText
                    style={{
                      fontSize: 12,
                      color: currentTheme?.theme?.font_color,
                      lineHeight: 18,
                    }}
                  >
                    {t("pricing.enterpriseMaxSpendWarning", {
                      minUoI: formatNumber(
                        convertMaxUsersToUoI(
                          "enterprise",
                          getActiveWorkspace()?.maxUsers || 10,
                        ),
                      ),
                    })}
                  </AppText>
                </View>
              )}
            </View>
          </AppChoice>
        ) : (
          <AppPricingScreen
            onPress={(sub) => {
              setSelectedPlan(sub?.type);
            }}
            credit={user?.stripeCustomerData?.balance / -100}
            isBillingDisabled={isBillingDisabled}
            isUpdateRequired={modalContentKey !== "UPDATE" || updateRequired}
            isUpdate={modalContentKey === "UPDATE"}
            currentPlan={
              modalContentKey !== "UPDATE"
                ? initialValues.subscriptionType
                : getActiveWorkspace()?.subscriptionType
            }
            onSeatsChange={(seats, subscriptionType) => {
              const currentUoI = convertMaxUsersToUoI(
                getActiveWorkspace()?.subscriptionType,
                getActiveWorkspace()?.maxUsers,
              );
              if (
                subscriptionType !== getActiveWorkspace()?.subscriptionType ||
                seats !== currentUoI
              ) {
                setUpdateRequired(true);
              } else {
                setUpdateRequired(false);
              }
            }}
            selectedSubscriptionType={selectedPlan}
            initialSeats={
              modalContentKey === "UPDATE"
                ? convertMaxUsersToUoI(
                    getActiveWorkspace()?.subscriptionType,
                    getActiveWorkspace()?.maxUsers || 1,
                  )
                : initialValues.maxUsers || DEFAULT_NEW_WORKSPACE_SEATS
            }
            customUoiPrice={getActiveWorkspace()?.customUoiPrice || null}
            reservedComputeAllocation={
              modalContentKey === "UPDATE"
                ? getActiveWorkspace()?.reservedComputeAllocation ||
                  values?.reservedComputeAllocation ||
                  false
                : false
            }
            enterpriseMinUoI={
              modalContentKey === "UPDATE" &&
              getActiveWorkspace()?.subscriptionType === "enterprise"
                ? getActiveWorkspace()?.customUoiBudget ||
                  convertMaxUsersToUoI(
                    "enterprise",
                    getActiveWorkspace()?.maxUsers || 10,
                  )
                : null
            }
            onUpdatePlan={async (
              subscriptionType,
              seats,
              isBillOverCredit,
              unitPrice,
            ) => {
              const activeWorkspace = getActiveWorkspace();
              if (
                modalContentKey === "UPDATE" &&
                (activeWorkspace?.reservedComputeAllocation ||
                  values?.reservedComputeAllocation) &&
                seats !==
                  convertMaxUsersToUoI(
                    activeWorkspace?.subscriptionType,
                    activeWorkspace?.maxUsers,
                  )
              ) {
                AppToasty.show(t("pricing.reservedComputeAllocationToast"), {
                  type: "danger",
                });
                setContactRequired(true);
                return;
              }
              const isEnterpriseCurrentPlan =
                subscriptionType === "enterprise" &&
                modalContentKey === "UPDATE" &&
                getActiveWorkspace()?.subscriptionType === "enterprise";

              if (
                subscriptionType !== "enterprise" &&
                isBillingDisabled &&
                isBillOverCredit
              ) {
                history.push("/profile/billing");
              } else if (
                modalContentKey !== "UPDATE" &&
                subscriptionType !== "enterprise"
              ) {
                onPlanSelection(subscriptionType, seats);
                setShowPlan(false);
                return;
              }
              if (
                subscriptionType === "enterprise" &&
                !isEnterpriseCurrentPlan
              ) {
                setContactRequired(true);
              } else {
                setConfirmationRequired(true);
                setTempPayload({
                  subscriptionType,
                  seats,
                  unitPrice,
                  isEnterpriseBudgetIncrease: isEnterpriseCurrentPlan,
                });
              }
            }}
          />
        )}
      </ToothFairyModal>
    ) : null;
    function capitalizeFirstCharacter(str) {
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    function decapitalizeButFirst(str) {
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    const parentWorkspaceItems = useMemo(
      () => [
        {
          title: t("workspaces.form.noParentWorkspace", {
            defaultValue: "No parent workspace",
          }),
          id: "none",
        },
        ...eligibleParentWorkspaces.map((ws) => ({
          title: ws.title,
          id: ws.id,
        })),
      ],
      [eligibleParentWorkspaces, t],
    );

    const domainItems = useMemo(
      () =>
        [
          ...workspaceCreationData?.workspaceDomain?.fieldOptions,
          ...customDomains,
        ]?.map((u) => ({
          ...u,
          title: t(`workspaces.form.domainOptions.${u?.id}`, {
            defaultValue: decapitalizeButFirst(u?.title),
          }),
        })),
      [workspaceCreationData, customDomains, t],
    );

    const visibilityItems = useMemo(
      () => [
        { name: t("workspaces.form.visibilityOptions.private"), id: "private" },
        { name: t("workspaces.form.visibilityOptions.public"), id: "public" },
        {
          name: t("workspaces.form.visibilityOptions.incognito"),
          id: "incognito",
        },
      ],
      [t],
    );

    const autoInviteRoleItems = useMemo(
      () => [
        { name: t("workspaces.form.roleOptions.admin"), id: "SuperAdmin" },
        { name: t("workspaces.form.roleOptions.aiEngineer"), id: "AIEngineer" },
        {
          name: t("workspaces.form.roleOptions.contentAdmin"),
          id: "ContentAdmin",
        },
        { name: t("workspaces.form.roleOptions.user"), id: "User" },
      ],
      [t],
    );

    return (
      <>
        {_modal}
        {!isNew &&
        (!values?.workspaceName || values?.workspaceName?.trim() === "") ? (
          <ToothFairyLoadingPlaceholder caption="messages.loadingWorkspaceDetails" />
        ) : (
          <>
            {modalContentKey === "UPDATE" && (
              <>
                <ImagePlaceholder
                  onFileSelection={handleLogoSelection}
                  imagePath={workspaceLogo}
                  isFileUploadInProgress={fileUploadInProgress}
                  fileUploadProgress={fileUploadProgress}
                  placeholderText={t("workspaces.form.uploadLogo")}
                  isLargeLogo={true}
                  forcedImgRadius={10}
                  wrapperStyle={{
                    marginBottom: 8,
                    alignSelf: "center",
                  }}
                />
                <AppText
                  style={{
                    marginBottom: 20,
                    textAlign: "center",
                    fontSize: 10,
                  }}
                  wrapperStyle={{
                    justifyContent: "center",
                    marginTop: 5,
                  }}
                  color={currentTheme?.theme?.mediumGrey}
                >
                  {t("workspaces.form.logoRatioHint", {
                    defaultValue:
                      "Recommended: wide horizontal logo (~6:1, e.g. 460x80 px). Square icons are still supported.",
                  })}
                </AppText>
              </>
            )}
            <AppForms.AppFormField
              showTextInputLabel
              label={t("workspaces.form.name")}
              isMandatory
              name={"workspaceName"}
              placeholder={t("workspaces.form.workspaceNamePlaceholder")}
              textContainer={{
                paddingLeft: 10,
                marginBottom: modalContentKey !== "UPDATE" && -2,
              }}
              textInputLabelStyle={{
                marginBottom: 0,
              }}
            />
            {isNew && (
              <AppForms.AppFormDropDown
                items={parentWorkspaceItems}
                showTextInputLabel
                isElevated={false}
                label={t("workspaces.form.parentWorkspace", {
                  defaultValue: "Parent Workspace",
                })}
                name={"parentWorkspaceID"}
                propertyValue={"id"}
                propertyLabel="title"
                inputLabelStyle={{
                  marginBottom: 5,
                  marginLeft: 5,
                }}
              />
            )}
            {isNew && eligibleParentWorkspaces.length === 0 && (
              <AppText
                style={{
                  fontSize: 12,
                  marginTop: -5,
                  marginBottom: 10,
                  marginLeft: 5,
                }}
                color={currentTheme?.theme?.secondary_font_color}
              >
                {t("workspaces.form.noEligibleParentWorkspaces", {
                  defaultValue:
                    "No business or enterprise workspaces available to use as parent.",
                })}
              </AppText>
            )}
            {isChildWorkspace ? (
              <View>
                <AppTextInput
                  editable={false}
                  showTextInputLabel
                  label={t("workspaces.form.inheritedPlan", {
                    defaultValue: "Inherited Plan",
                  })}
                  value={`${capitalizeFirstCharacter(
                    utils.adaptSubscriptionName(
                      parentWorkspace?.subscriptionType,
                    ),
                  )} (${t("workspaces.form.billingManagedByParent", {
                    defaultValue: "billing managed by parent",
                  })})`}
                  textContainer={{
                    paddingHorizontal: 10,
                    marginHorizontal: 5,
                  }}
                  style={{
                    marginHorizontal: 0,
                  }}
                />
                <AppText
                  style={{
                    fontSize: 12,
                    marginTop: 5,
                    marginBottom: 10,
                    marginHorizontal: 10,
                    maxWidth: 400,
                  }}
                  color={currentTheme?.theme?.orange}
                >
                  {t("workspaces.form.planManagedByRoot", {
                    name: parentWorkspace?.title || "",
                    defaultValue:
                      'Plan selection is disabled. Please refer to the root workspace "{{name}}" for billing and plan management.',
                  })}
                </AppText>
              </View>
            ) : (
              <AppTextInput
                editable={false}
                showTextInputLabel
                isRightLabelFixed={true}
                label={t("workspaces.form.choosePlan")}
                name={
                  isChangeOfSubRequested && !isNew
                    ? "targetSubscriptionType"
                    : "subscriptionType"
                }
                rightIconLabel={
                  modalContentKey === "UPDATE"
                    ? getEffectiveSubscriptionType(getActiveWorkspace()) ===
                      "enterprise"
                      ? undefined
                      : t("pricing.upgrade", { defaultValue: "Upgrade" })
                    : t("common.select")
                }
                value={
                  values?.subscriptionType !== "Select Plan"
                    ? ENABLE_CONSUMPTION_BILLING &&
                      ["base", "pro"].includes(values?.subscriptionType)
                      ? capitalizeFirstCharacter(
                          utils.adaptSubscriptionName("business"),
                        )
                      : values?.subscriptionType == "base"
                      ? "Individual"
                      : values?.subscriptionType == "pro"
                      ? "Individual"
                      : `${capitalizeFirstCharacter(
                          utils.adaptSubscriptionName(values?.subscriptionType),
                        )}`
                    : t("workspaces.form.selectPlan")
                }
                labelStyle={{
                  fontWeight: "bold",
                }}
                textContainer={{
                  paddingHorizontal: 10,
                  marginHorizontal: 5,
                }}
                style={{
                  marginHorizontal: 0,
                }}
                textInputLabelStyle={{
                  marginBottom: 0,
                }}
                onRightIconClick={() => {
                  if (values?.reservedComputeAllocation) {
                    AppToasty.show(
                      t("pricing.reservedComputeAllocationToast"),
                      { type: "danger" },
                    );
                    return;
                  }
                  // Enterprise is the top tier — no plan change is available, so
                  // the "Edit" affordance is hidden entirely for enterprise
                  // workspaces (see rightIconLabel gate). Guard here too.
                  if (
                    modalContentKey === "UPDATE" &&
                    getEffectiveSubscriptionType(getActiveWorkspace()) ===
                      "enterprise"
                  ) {
                    return;
                  }
                  setShowPlan(true);
                }}
                rightIcon={AppAssets?.icons?.arrow_right}
                rightIconSize={16}
                rightIconStyle={{
                  position: "relative",
                  left: 8,
                  alignItems: "center",
                }}
              />
            )}

            {/* <AppForms.AppFormDropDown
        items={workspaceCreationData?.workspaceBaseModel?.fieldOptions}
        showTextInputLabel
        isElevated={false}
        disabled={modalContentKey === "UPDATE"}
        label="Sentiment reviewer model"
        name={"baseModel"}
        propertyValue={"id"}
        propertyLabel="title"
      /> */}
            <AppForms.AppFormDropDown
              items={domainItems}
              showTextInputLabel
              isElevated={false}
              label={t("workspaces.form.businessType")}
              name={"domain"}
              propertyValue={"id"}
              propertyLabel="title"
              inputLabelStyle={{
                marginBottom: 5,
                marginLeft: 5,
              }}
            />
            {modalContentKey == "UPDATE" ? (
              <>
                <AppForms.AppFormDropDown
                  isElevated={false}
                  showTextInputLabel
                  label={t("settings.workspace.defaultChatAgentLabel")}
                  name="defaultChatAgent"
                  items={operatorAgents}
                  propertyLabel="name"
                  propertyValue="id"
                  inputLabelStyle={{
                    marginBottom: 5,
                    marginLeft: 5,
                  }}
                />
                <AppForms.AppFormDropDown
                  isElevated={false}
                  showTextInputLabel
                  label={t("workspaces.form.defaultChatVisibility")}
                  name="defaultChatVisibility"
                  items={visibilityItems}
                  propertyLabel="name"
                  propertyValue="id"
                  inputLabelStyle={{
                    marginBottom: 5,
                    marginLeft: 5,
                  }}
                />
                <AppSeparator
                  text={t("workspaces.form.modelGovernance")}
                  separatorContainer={{
                    marginTop: 50,
                  }}
                />
                <AppForms.AppFormSwitch
                  disabled={
                    getEffectiveSubscriptionType(getActiveWorkspace()) == "base"
                  }
                  wrapperStyle={[
                    {
                      // backgroundColor: currentTheme?.theme?.grey,

                      maxHeight: 30,
                      paddingHorizontal: 0,

                      paddingVertical: 18,
                      margin: 5,
                      marginTop: 0,
                    },
                  ]}
                  labelTextStyle={{
                    fontSize: currentTheme?.theme?.small_font_size,
                    color: currentTheme?.theme?.darkGrey,
                  }}
                  showTextInputLabel={true}
                  label={t("workspaces.form.enforceRegionalModels")}
                  displayIsTicked={true}
                  name="enforceRegionalModels"
                  value={values?.enforceRegionalModels}
                  description={t("workspaces.form.enforceRegionalModelsDesc")}
                  descriptionIcon={false}
                  descriptionColor={currentTheme?.theme?.mediumGrey}
                />
                <AppForms.AppFormMultiSelect
                  showTextInputLabel
                  label={t("workspaces.form.blockedModels")}
                  name="blockedModels"
                  placeholder={t("workspaces.form.blockedModelsPlaceholder")}
                  hidePlaceholder={true}
                  items={_blockableModels}
                  uniqueKey="id"
                  showDropDowns={true}
                  readOnlyHeadings={true}
                  labelOption="name"
                  style={{ maxWidth: 800 }}
                  wrapperContainerStyle={{
                    zIndex: 20,
                    marginTop: 40,
                    marginBottom: 0,
                    marginHorizontal: 5,
                    maxWidth: 667,
                  }}
                  styles={colourStyles}
                  options={_blockableModels.map((u) => {
                    return {
                      ...u,
                      label: u?.name || u?.id,
                      value: u?.id,
                      backgroundColor: colorsMappings.mapBackgroundColorWithModelSize(
                        u,
                      ),
                    };
                  })}
                />
                <AppForms.AppFormSwitch
                  disabled={
                    getActiveWorkspace()?.subscriptionType != "enterprise"
                  }
                  wrapperStyle={[
                    {
                      // backgroundColor: currentTheme?.theme?.grey,

                      maxHeight: 30,
                      paddingHorizontal: 0,

                      paddingVertical: 18,
                      margin: 5,
                      marginTop: 10,
                    },
                  ]}
                  labelTextStyle={{
                    fontSize: currentTheme?.theme?.small_font_size,
                    color: currentTheme?.theme?.darkGrey,
                  }}
                  showTextInputLabel={true}
                  label={t("workspaces.form.enforceMFA")}
                  displayIsTicked={true}
                  name="isMFAEnabled"
                  value={values?.isMFAEnabled}
                  description={t("workspaces.form.enforceMFADesc")}
                  descriptionIcon={false}
                  descriptionColor={currentTheme?.theme?.mediumGrey}
                />
                {(getActiveWorkspace()?.subscriptionType == "enterprise" ||
                  getActiveWorkspace()?.subscriptionType == "business") && (
                  <>
                    <AppSeparator
                      text={t("workspaces.form.teamManagement")}
                      separatorContainer={{
                        marginTop: 50,
                      }}
                    />
                    <AppForms.AppFormSwitch
                      wrapperStyle={[
                        {
                          // backgroundColor: currentTheme?.theme?.grey,

                          maxHeight: 30,
                          paddingHorizontal: 0,

                          paddingVertical: 18,
                          margin: 5,
                          marginTop: 10,
                          marginBottom: 20,
                        },
                      ]}
                      labelTextStyle={{
                        fontSize: currentTheme?.theme?.small_font_size,
                        color: currentTheme?.theme?.darkGrey,
                      }}
                      showTextInputLabel={true}
                      label={t("workspaces.form.enableAutoInvite")}
                      displayIsTicked={true}
                      description={t("workspaces.form.enableAutoInviteDesc")}
                      descriptionIcon={false}
                      descriptionColor={currentTheme?.theme?.mediumGrey}
                      name="enableAutoInvite"
                      value={values?.enableAutoInvite}
                    />

                    {values?.enableAutoInvite && (
                      <>
                        <AppForms.AppFormField
                          showTextInputLabel
                          label={t("workspaces.form.autoInviteDomain")}
                          name={"autoInviteDomain"}
                          placeholder={t("workspaces.form.domainPlaceholder")}
                          textContainer={{
                            paddingLeft: 10,
                          }}
                          textInputLabelStyle={{
                            marginLeft: 7,
                            marginTop: 20,
                          }}
                        />
                        <AppForms.AppFormDropDown
                          isElevated={false}
                          showTextInputLabel
                          label={t("workspaces.form.autoInviteRole")}
                          name="autoInviteUserRole"
                          items={autoInviteRoleItems}
                          propertyLabel="name"
                          propertyValue="id"
                        />
                      </>
                    )}
                  </>
                )}

                {(getActiveWorkspace()?.subscriptionType == "enterprise" ||
                  getActiveWorkspace()?.subscriptionType == "business") && (
                  <>
                    <AppSeparator
                      text={t("workspaces.form.billingAndCompute", {
                        defaultValue: "Billing & compute",
                      })}
                      separatorContainer={{
                        marginTop: 20,
                        marginBottom: 10,
                      }}
                    />
                    {ENABLE_CONSUMPTION_BILLING &&
                      !isChildWorkspace &&
                      (getEffectiveSubscriptionType(getActiveWorkspace()) ===
                        "business" ||
                        getEffectiveSubscriptionType(getActiveWorkspace()) ===
                          "enterprise") &&
                      !(
                        getActiveWorkspace()?.reservedComputeAllocation ||
                        values?.reservedComputeAllocation
                      ) && (
                        <View
                          style={{
                            paddingHorizontal: 0,
                            marginHorizontal: 0,
                            marginVertical: 8,
                            marginBottom: 20,
                          }}
                        >
                          <AppForms.AppFormTrackSlider
                            name="maxUsers"
                            label={t("workspaces.form.uoiAllocation", {
                              defaultValue: "Budget allocation per workspace",
                            })}
                            containerStyle={{
                              paddingHorizontal: 5,
                            }}
                            step={1}
                            minimumValue={1}
                            maximumValue={Math.max(
                              Math.ceil(
                                MAX_WORKSPACE_UOI_BUDGET /
                                  (UOI_PER_BLOCK[
                                    getEffectiveSubscriptionType(
                                      getActiveWorkspace(),
                                    )
                                  ] || UOI_PER_BLOCK.business),
                              ),
                              Number(getActiveWorkspace()?.maxUsers) || 1,
                            )}
                            visualMultiplier={
                              UOI_PER_BLOCK[
                                getEffectiveSubscriptionType(
                                  getActiveWorkspace(),
                                )
                              ] || UOI_PER_BLOCK.business
                            }
                            formatValue={formatUoICompact}
                            parseValue={parseUoICompact}
                            unitLabel={` ${t("credits.indicator.uoi", {
                              defaultValue: "UoI",
                            })}`}
                            wrapperStyle={{ width: "100%" }}
                            description={t(
                              "workspaces.form.uoiAllocationHint",
                              {
                                block: formatUoICompact(
                                  UOI_PER_BLOCK[
                                    getEffectiveSubscriptionType(
                                      getActiveWorkspace(),
                                    )
                                  ] || UOI_PER_BLOCK.business,
                                ),
                                defaultValue:
                                  "Usage budget for this workspace. Multiples of {{block}} UoI (1 UoI = $0.01). Increasing it re-enables usage immediately if paused.",
                              },
                            )}
                            descriptionIcon={false}
                            descriptionColor={currentTheme?.theme?.mediumGrey}
                          />
                        </View>
                      )}
                    <View
                      style={{
                        paddingHorizontal: 0,
                        marginHorizontal: 5,
                        marginVertical: 8,
                        minHeight: 30,
                        justifyContent: "center",
                      }}
                    >
                      <AppText
                        style={{
                          fontSize: currentTheme?.theme?.small_font_size,
                          color: currentTheme?.theme?.darkGrey,
                          marginBottom: 2,
                        }}
                      >
                        {t("workspaces.form.billingProvider", {
                          defaultValue: "Billing provider",
                        })}
                      </AppText>
                      <AppText
                        style={{
                          fontSize: currentTheme?.theme?.font_size,
                          color: currentTheme?.theme?.font_color,
                          fontWeight: "bold",
                        }}
                      >
                        {(() => {
                          const provider =
                            getActiveWorkspace()?.billingProvider ||
                            values?.billingProvider ||
                            "STRIPE";
                          return provider === "XERO" ? "Xero" : "Stripe";
                        })()}
                      </AppText>
                    </View>
                    {getActiveWorkspace()?.supportHours > 0 && (
                      <View
                        style={{
                          paddingHorizontal: 0,
                          marginHorizontal: 5,
                          marginVertical: 8,
                          minHeight: 30,
                          justifyContent: "center",
                        }}
                      >
                        <AppText
                          style={{
                            fontSize: currentTheme?.theme?.small_font_size,
                            color: currentTheme?.theme?.darkGrey,
                            marginBottom: 2,
                          }}
                        >
                          {t("workspaces.form.supportHours", {
                            defaultValue: "Support hours / month",
                          })}
                        </AppText>
                        <AppText
                          style={{
                            fontSize: currentTheme?.theme?.font_size,
                            color: currentTheme?.theme?.font_color,
                            fontWeight: "bold",
                          }}
                        >
                          {String(
                            getActiveWorkspace()?.supportHours ??
                              values?.supportHours ??
                              0,
                          )}
                        </AppText>
                      </View>
                    )}

                    <View
                      style={{
                        paddingHorizontal: 0,
                        marginHorizontal: 5,
                        marginVertical: 8,
                        marginBottom: 20,
                        minHeight: 30,
                        justifyContent: "center",
                      }}
                    >
                      <AppText
                        style={{
                          fontSize: currentTheme?.theme?.small_font_size,
                          color: currentTheme?.theme?.darkGrey,
                          marginBottom: 2,
                        }}
                      >
                        {t("workspaces.form.reservedComputeAllocation", {
                          defaultValue: "Reserved compute allocation",
                        })}
                      </AppText>
                      <AppText
                        style={{
                          fontSize: currentTheme?.theme?.font_size,
                          color: currentTheme?.theme?.font_color,
                          fontWeight: "bold",
                        }}
                      >
                        {getActiveWorkspace()?.reservedComputeAllocation ||
                        values?.reservedComputeAllocation
                          ? t("workspaces.form.reservedYes", {
                              defaultValue: "Yes",
                            })
                          : t("workspaces.form.reservedNo", {
                              defaultValue: "No",
                            })}
                      </AppText>
                    </View>
                    {(getActiveWorkspace()?.reservedComputeAllocation ||
                      values?.reservedComputeAllocation) && (
                      <AppText
                        style={{
                          fontSize: 12,
                          marginTop: -10,
                          marginBottom: 10,
                          marginHorizontal: 5,
                        }}
                        color={currentTheme?.theme?.orange}
                      >
                        {t("workspaces.form.reservedComputeAllocationHint", {
                          defaultValue:
                            "UoI / maxUsers is reserved and cannot be adjusted here. Contact us to change it.",
                        })}
                      </AppText>
                    )}
                  </>
                )}
              </>
            ) : (
              <></>
            )}

            {modalContentKey !== "UPDATE" && (
              <AppText
                wrapperStyle={{
                  marginTop: -10,
                  maxWidth: 340,
                  textAlign: "center",
                }}
                color={currentTheme?.theme?.primary}
              >
                {t("workspaces.form.businessTypeHint")}
              </AppText>
            )}

            {(isMessageVisible || isChangeCancellable) && (
              <View
                style={{
                  maxWidth: 340,
                  padding: 10,
                  alignItems: "center",
                }}
              >
                {isMessageVisible && (
                  <AppText
                    preventTranslation={true}
                    color={currentTheme?.theme?.orange}
                  >
                    {canUpdateSubscription()
                      ? t("workspaces.planTransitionImmediate", {
                          plan: _targetPlan,
                        })
                      : t("workspaces.planTransitionNextMonth", {
                          plan:
                            values?.targetSubscriptionType ||
                            values.subscriptionType,
                        })}
                  </AppText>
                )}
                {isChangeCancellable && (
                  <AppText
                    isClickable={true}
                    preventTranslation={true}
                    onPress={() => cancelPlanChange()}
                    style={{
                      fontWeight: "bold",
                      color: currentTheme?.theme?.blue,
                    }}
                  >
                    {t("workspaces.clickHereToCancel")}
                  </AppText>
                )}
              </View>
            )}
          </>
        )}
      </>
    );
  },
);

export default WorkspaceFormView;
